// eslint-disable-next-line import/no-unresolved
import _XRegExp, { exec, test, ReplacementValue, ExecArray } from 'xregexp';

type AugmentedXRegExp = typeof _XRegExp & {
  execLb: (str: string, lb: string, regex: RegExp) => ReturnType<typeof exec>;
  testLb: (str: string, lb: string, regex: RegExp) => ReturnType<typeof test>;
  searchLb: (str: string, lb: string, regex: RegExp) => number;
  matchAllLb: (str: string, lb: string, regex: RegExp) => Array<string>;
  replaceLb: (
    str: string,
    lb: string,
    regex: RegExp,
    replacement: ReplacementValue
  ) => string;
  replaceLbIgnoreRespellings: (
    str: string,
    lb: string,
    regex: RegExp,
    replacement: ReplacementValue
  ) => string;
  getLbIgnoreRespellingsPositions: (
    str: string,
    regex: RegExp
  ) => Array<{ from: number; to: number }>;
};

/**
 * Augmenting the XRegExp class with custom implemenations of the following "lookbehind"
 * methods. This is needed due to poor cross-browser compatibility of the native js lookbehind
 * feature of regex.
 *
 * @see https://caniuse.com/js-regexp-lookbehind
 * @see https://blog.stevenlevithan.com/archives/javascript-regex-lookbehind
 */
// eslint-disable-next-line no-shadow
export default (function augmentXRegExp(XRegExp): AugmentedXRegExp {
  function prepareLb(lb: string) {
    // Allow mode modifier before lookbehind
    const parts = /^((?:\(\?[\w$]+\))?)\(\?<([=!])([\s\S]*)\)$/.exec(lb);
    return {
      // $(?!\s) allows use of (?m) in lookbehind
      // eslint-disable-next-line prefer-template
      lb: XRegExp(parts ? parts[1] + '(?:' + parts[3] + ')$(?!\\s)' : lb),
      // Positive or negative lookbehind. Use positive if no lookbehind group
      type: parts ? parts[2] === '=' : !parts,
    };
  }

  function execLb(str: string, lb: string, regex: RegExp) {
    let pos = 0;
    let match: ExecArray | null;
    let leftContext: string;
    const lbPrepared = prepareLb(lb);
    // eslint-disable-next-line no-cond-assign
    while ((match = XRegExp.exec(str, regex, pos))) {
      leftContext = str.slice(0, match.index);
      if (lbPrepared.type === lbPrepared.lb.test(leftContext)) {
        return match;
      }
      pos = match.index + 1;
    }
    return null;
  }

  function testLb(str: string, lb: string, regex: RegExp) {
    return !!execLb(str, lb, regex);
  }

  function searchLb(str: string, lb: string, regex: RegExp) {
    const match = execLb(str, lb, regex);
    return match ? match.index : -1;
  }

  function matchAllLb(str: string, lb: string, regex: RegExp) {
    const matches = [];
    let pos = 0;
    let match: ExecArray | null;
    let leftContext: string;
    const preparedLb = prepareLb(lb);
    // eslint-disable-next-line no-cond-assign
    while ((match = XRegExp.exec(str, regex, pos))) {
      leftContext = str.slice(0, match.index);
      if (preparedLb.type === preparedLb.lb.test(leftContext)) {
        matches.push(match[0]);
        pos = match.index + (match[0].length || 1);
      } else {
        pos = match.index + 1;
      }
    }
    return matches;
  }

  function replaceLbIgnoreRespellings(
    str: string,
    lb: string,
    regex: RegExp,
    replacement: ReplacementValue
  ) {
    let output = '';
    let pos = 0;
    let lastEnd = 0;
    let match: ExecArray | null;
    let leftContext: string;
    const preparedLb = prepareLb(lb);
    const phoneticSpellingRegex = XRegExp(`::[a-zA-Z\\-]+::`);
    // eslint-disable-next-line no-cond-assign
    while ((match = XRegExp.exec(str, regex, pos))) {
      // exclude the match if it's a match within phonetic spellings
      const respellingMatch = XRegExp.exec(str, phoneticSpellingRegex, pos);
      if (respellingMatch && respellingMatch.index <= match.index) {
        const newEnd = respellingMatch.index + respellingMatch[0].length;
        output += str.slice(lastEnd, newEnd);
        lastEnd = newEnd;
        pos = respellingMatch.index + (respellingMatch[0].length || 1);
        // eslint-disable-next-line no-continue
        continue;
      }
      leftContext = str.slice(0, match.index);
      if (preparedLb.type === preparedLb.lb.test(leftContext)) {
        // Doesn't work correctly if lookahead in regex looks outside of the match
        output +=
          str.slice(lastEnd, match.index) +
          XRegExp.replace(match[0], regex, replacement);
        lastEnd = match.index + match[0].length;
        if (!regex.global) {
          break;
        }
        pos = match.index + (match[0].length || 1);
      } else {
        pos = match.index + 1;
      }
    }
    return output + str.slice(lastEnd);
  }

  function replaceLb(
    str: string,
    lb: string,
    regex: RegExp,
    replacement: ReplacementValue
  ) {
    let output = '';
    let pos = 0;
    let lastEnd = 0;
    let match: ExecArray | null;
    let leftContext: string;
    const preparedLb = prepareLb(lb);
    // eslint-disable-next-line no-cond-assign
    while ((match = XRegExp.exec(str, regex, pos))) {
      leftContext = str.slice(0, match.index);
      if (preparedLb.type === preparedLb.lb.test(leftContext)) {
        // Doesn't work correctly if lookahead in regex looks outside of the match
        output +=
          str.slice(lastEnd, match.index) +
          XRegExp.replace(match[0], regex, replacement);
        lastEnd = match.index + match[0].length;
        if (!regex.global) {
          break;
        }
        pos = match.index + (match[0].length || 1);
      } else {
        pos = match.index + 1;
      }
    }
    return output + str.slice(lastEnd);
  }

  /**
   * Find the exact to, from character indices of where match replacements will occur
   */
  function getLbIgnoreRespellingsPositions(str: string, regex: RegExp) {
    let pos = 0;
    let lastEnd = 0;
    let match: ExecArray | null;
    let leftContext: string;
    // an always-true lookbehind; the leading word boundary lives in `regex`
    // @see replacementRegex
    const lb = '(?<=)';
    const replacedIndices = [];
    const preparedLb = prepareLb(lb);
    const phoneticSpellingRegex = XRegExp(`::[a-zA-Z\\-]+::`);
    // eslint-disable-next-line no-cond-assign
    while ((match = XRegExp.exec(str, regex, pos))) {
      // exclude the match if it's a match within phonetic spellings
      // Todo: is this line still needed?
      const respellingMatch = XRegExp.exec(str, phoneticSpellingRegex, pos);
      if (respellingMatch && respellingMatch.index <= match.index) {
        const newEnd = respellingMatch.index + respellingMatch[0].length;
        lastEnd = newEnd;
        pos = respellingMatch.index + (respellingMatch[0].length || 1);
        // eslint-disable-next-line no-continue
        continue;
      }
      leftContext = str.slice(0, match.index);
      if (preparedLb.type === preparedLb.lb.test(leftContext)) {
        lastEnd = match.index + match[0].length;
        replacedIndices.push({ from: match.index, to: lastEnd });
        if (!regex.global) {
          break;
        }
        pos = match.index + (match[0].length || 1);
      } else {
        pos = match.index + 1;
      }
    }
    return replacedIndices;
  }

  return Object.assign(XRegExp, {
    execLb,
    testLb,
    searchLb,
    matchAllLb,
    replaceLb,
    replaceLbIgnoreRespellings,
    getLbIgnoreRespellingsPositions,
  });
})(_XRegExp);
